import { useTranslation } from 'react-i18next';
import { ClockClockwise } from '@phosphor-icons/react';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';
import { isAttendanceInProgress, isOpenRecord } from '../../services/attendance-rules';

/** Where a day was worked (`Attendance.Status`): from home (clocked in from the app or written by an
 * approved correction) or from the office (imported from the office door / device log). */
export type WorkStatus = 'WFH' | 'WFO';

export const WORK_STATUSES: WorkStatus[] = ['WFH', 'WFO'];

/** Every state a day can be shown in: worked from home or office, still being worked, left open past
 * its shift (a forgotten clock-out), a holiday, leave, leave waiting for approval, a day off, a missed
 * working day, or (on a list of records only) no record at all. */
export type DayDisplayStatus =
  | WorkStatus
  | 'Working'
  | 'MissingClockOut'
  | 'Holiday'
  | 'Leave'
  | 'LeavePending'
  | 'Weekend'
  | 'Absent'
  | 'NoRecord';

/** Outlined chips: the status color on the border and text, no fill. */
const STYLE: Record<DayDisplayStatus, string> = {
  WFH: 'border-success text-success',
  WFO: 'border-primary-600 text-primary-600',
  Working: 'border-primary-600 text-primary-600',
  MissingClockOut: 'border-error text-error',
  Holiday: 'border-warning text-warning',
  Leave: 'border-warning text-warning',
  LeavePending: 'border-dashed border-warning text-warning',
  Weekend: 'border-muted-foreground text-muted-foreground',
  Absent: 'border-error text-error',
  NoRecord: 'border-muted-foreground text-muted-foreground',
};

/** The translation key of each status's label. */
export const DAY_STATUS_LABEL_KEY: Record<DayDisplayStatus, string> = {
  WFH: 'DAY_STATUS_WFH',
  WFO: 'DAY_STATUS_WFO',
  Working: 'DAY_STATUS_WORKING',
  MissingClockOut: 'DAY_STATUS_MISSING_CLOCK_OUT',
  Holiday: 'DAY_STATUS_HOLIDAY',
  Leave: 'DAY_STATUS_LEAVE',
  LeavePending: 'DAY_STATUS_LEAVE_PENDING',
  Weekend: 'DAY_STATUS_WEEKEND',
  Absent: 'DAY_STATUS_ABSENT',
  NoRecord: 'NO_RECORD',
};

/** The status a record is shown with. An open day is "Working" only while its session can still be
 * running; an older one is a forgotten clock-out (no FORCE_CLOCK_OUT_* rule closed it), waiting for a
 * correction. A completed day shows where it was worked; one saved before `Status` existed shows
 * nothing. */
export const dayDisplayStatusOf = (
  record:
    | {
        StartTimeUtc?: string | null;
        EndTimeUtc?: string | null;
        BreakTimeDetails?:
          { StartTimeUtc: string; EndTimeUtc?: string | null; Type?: string | null }[] | null;
        Status?: string | null;
      }
    | null
    | undefined,
  now: Date = new Date()
): DayDisplayStatus | null => {
  if (!record?.StartTimeUtc) return 'NoRecord';
  if (isOpenRecord(record))
    return isAttendanceInProgress(record, now) ? 'Working' : 'MissingClockOut';
  if (record.Status === 'WFH' || record.Status === 'WFO') return record.Status;
  return null;
};

export const DayStatusBadge = ({
  status,
  label,
  forced,
  className,
}: {
  status: DayDisplayStatus | null;
  /** Shown instead of the status's own label (a holiday's or a leave's name). */
  label?: string;
  /** Clocked out by the scheduler (FORCE_CLOCK_OUT_* rules). */
  forced?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  if (!status && !forced) return <span className="text-muted-foreground">-</span>;
  const text = status ? label || t(DAY_STATUS_LABEL_KEY[status]) : '';
  return (
    <span className={cn('inline-flex max-w-full flex-col items-start gap-1', className)}>
      {status && (
        // Capped at its cell's width: a long holiday or leave name ends in "…", and the app's
        // OverflowTooltip shows the whole name on hover (or tap) only when it is cut off.
        <Badge
          variant="outline"
          className={cn('max-w-full bg-transparent font-medium', STYLE[status])}
        >
          <span className="truncate">{text}</span>
        </Badge>
      )}
      {forced && (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
          <ClockClockwise className="size-3.5 shrink-0" />
          {t('AUTO_CLOCKED_OUT')}
        </span>
      )}
    </span>
  );
};
